import React, { useEffect, useState, useRef } from "react";
import { useUser } from "reactfire";
import { useTranslation } from "react-i18next";
import { useGame } from "context/gameContext";
import { Purchases } from "@revenuecat/purchases-capacitor";

import SkeletonLoader from "components/skeletonLoader";
import { RadioGroup } from "@headlessui/react";
import { classNames, showToast } from "utils/utils";
import { CheckIcon } from "@heroicons/react/24/outline";
import { CheckBadgeIcon } from "@heroicons/react/24/solid";
import {
  IonButton,
  IonContent,
  IonFooter,
  IonHeader,
  IonItem,
  IonLabel,
  IonPage,
  IonPopover,
  IonRouterLink,
  IonTitle,
  IonToolbar,
} from "@ionic/react";
import {
  Md2ChevronDown,
  Md2ClockDigital,
  Md2ClockFast,
  Md2InformationSlabCircleOutline,
  Md2Loading,
} from "react-icons-ng/md2";
import AppLogo from "components/appLogo";
import { Capacitor } from "@capacitor/core";

export default function Paywall({ closeModal }) {
  const {
    state: { premium, theme, settings },
  } = useGame();
  const { data: user } = useUser();
  const { t } = useTranslation();
  const [offerList, setOfferList] = useState([]);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isPurchasing, setIsPurchasing] = useState(false);
  const [annualSaving, setAnnualSaving] = useState(undefined);

  let premiumSellingPoints = [
    {
      title: t("premium.sellingPoints.first"),
      basicComponent: (
        <div
          className="flex w-full items-center justify-between cursor-pointer"
          id="click-trigger-1"
        >
          <span className="w-full justify-end text-right pr-2">
            {
              settings?.offeredLeagueList?.filter((league) => !league?.premium)
                ?.length
            }
          </span>
          <Md2ChevronDown
            className="h-6 w-6 text-gray-500 shrink-0"
            slot="end"
          />
          <IonPopover
            trigger="click-trigger-1"
            triggerAction="click"
            alignment="center"
          >
            <IonContent>
              <div className="flex flex-col text-sm  justify-center py-2">
                {settings?.offeredLeagueList
                  ?.filter((league) => !league?.premium)
                  .map((league) => (
                    <div className="flex items-center h-8">
                      <img
                        src={league?.flag}
                        className="  rounded-sm w-6 object-contain	mr-2"
                        alt="Logo"
                      />
                      <div className="block uppercase tracking-wide text-black text-xs font-bold">
                        {league?.name}
                      </div>
                    </div>
                  ))}
              </div>
            </IonContent>
          </IonPopover>
        </div>
      ),
      premiumComponent: (
        <div
          className="flex w-full items-center justify-between cursor-pointer"
          id="click-trigger-2"
        >
          <span className="w-full justify-end text-right pr-2">
            {settings?.offeredLeagueList?.length}+
          </span>
          <Md2ChevronDown
            className="h-6 w-6 text-gray-500 shrink-0"
            slot="end"
          />
          <IonPopover
            trigger="click-trigger-2"
            triggerAction="click"
            alignment="center"
          >
            <IonContent>
              <div className="flex flex-col text-sm  justify-center py-2">
                {settings?.offeredLeagueList?.map((league) => (
                  <div className="flex items-center h-8">
                    <img
                      src={league?.flag}
                      className="  rounded-sm w-6 object-contain	mr-2"
                      alt="Logo"
                    />
                    <div className="block uppercase tracking-wide text-black text-xs font-bold">
                      {league?.name}
                    </div>
                  </div>
                ))}
                <div className="flex items-center h-8">
                  <Md2ClockFast className="h-6 w-6 mr-2 text-gray-500 shrink-0" />
                  <div className="block uppercase tracking-wide text-black text-xs font-bold">
                    More to come!
                  </div>
                </div>
              </div>
            </IonContent>
          </IonPopover>
        </div>
      ),
    },
    {
      title: t("premium.sellingPoints.second"),
      basicComponent: (
        <div
          className="flex w-full items-center justify-between cursor-pointer"
          id="click-trigger-2-basic"
        >
          <span className="w-full justify-end text-right pr-2">
            {" "}
            {
              settings?.offeredGameList?.filter((mode) => !mode?.premium)
                ?.length
            }
          </span>
          <Md2ChevronDown
            className="h-6 w-6 text-gray-500 shrink-0"
            slot="end"
          />
          <IonPopover
            trigger="click-trigger-2-basic"
            triggerAction="click"
            alignment="center"
          >
            <IonContent>
              <div className="flex flex-col text-sm  justify-center py-2">
                {settings?.offeredGameList
                  ?.filter((mode) => !mode?.premium)
                  .map((mode) => (
                    <div className="flex items-center h-8">
                      <div className="block uppercase tracking-wide text-black text-xs font-bold">
                        {mode?.name}
                      </div>
                    </div>
                  ))}
              </div>
            </IonContent>
          </IonPopover>
        </div>
      ),
      premiumComponent: (
        <div
          className="flex w-full items-center justify-between cursor-pointer"
          id="click-trigger-2-premium"
        >
          <span className="w-full justify-end text-right pr-4">
            {settings?.offeredGameList?.length}
          </span>
          <Md2ChevronDown
            className="h-6 w-6 text-gray-500 shrink-0"
            slot="end"
          />
          <IonPopover
            trigger="click-trigger-2-premium"
            triggerAction="click"
            alignment="center"
          >
            <IonContent>
              <div className="flex flex-col text-sm  justify-center py-2">
                {settings?.offeredGameList.map((mode) => (
                  <div className="flex items-center h-8">
                    <div className="block uppercase tracking-wide text-black text-xs font-bold">
                      {mode?.name}
                    </div>
                  </div>
                ))}
              </div>
            </IonContent>
          </IonPopover>
        </div>
      ),
    },
    {
      title: t("premium.sellingPoints.third"),
      basicComponent: (
        <span className="w-full text-center cursor-default">
          {t("premium.perMonth", { value: settings?.joinCreditMonth })}
        </span>
      ),
      premiumComponent: (
        <span className="w-full text-center cursor-default">
          {t("premium.unlimited")}
        </span>
      ),
    },
    {
      title: t("premium.sellingPoints.fourth"),
      basicComponent: (
        <span className="w-full text-center cursor-default">
          {t("premium.perMonth", { value: settings?.createCreditMonth })}
        </span>
      ),
      premiumComponent: (
        <span className="w-full text-center cursor-default">
          {t("premium.unlimited")}
        </span>
      ),
    },
    {
      title: t("premium.sellingPoints.fifth"),
      basicComponent: (
        <span className="w-full text-center cursor-default">
          {t("common.yes")}
        </span>
      ),
      premiumComponent: (
        <span className="w-full text-center cursor-default">
          {t("premium.ads_none")}
        </span>
      ),
    },
  ];

  useEffect(() => {
    listOffers();
  }, []);

  useEffect(() => {
    let monthlyPlan = offerList.find(
      (product) => product?.packageType == "MONTHLY"
    );
    let annualPlan = offerList.find(
      (product) => product?.packageType == "ANNUAL"
    );

    let savings =
      annualPlan?.product?.price && monthlyPlan?.product?.price
        ? (1 -
            annualPlan?.product?.price / (monthlyPlan?.product?.price * 12)) *
          100
        : 0;
    if (typeof savings == "number") {
      setAnnualSaving(savings);
    }
  }, [offerList]);

  async function listOffers() {
    setIsLoading(true);
    try {
      const offerings = await Purchases.getOfferings();

      if (
        offerings.current !== null &&
        offerings.current.availablePackages.length !== 0
      ) {
        const products = offerings?.current?.availablePackages;

        const sortedProducts = (products || []).slice().sort((x, y) => {
          return (y.product?.price || 0) - (x.product?.price || 0);
        });

        for (let i = 0; i < sortedProducts.length; i++) {
          const product = sortedProducts[i];
          const trialLengthValue = await checkIfTrialAvailable(
            product?.product
          );

          sortedProducts[i] = {
            ...product,
            trialAvailable: trialLengthValue > 0,
            trialLength: trialLengthValue,
          };
        }

        setOfferList(sortedProducts);
        if (sortedProducts.length > 0) {
          setSelectedOffer(sortedProducts[0]);
        }
      }
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.log(error);
    }
  }

  async function purchase(e) {
    if (!selectedOffer?.product) return;

    setIsPurchasing(true);
    e.preventDefault();
    try {
      const productToPurchase = {
        identifier: selectedOffer?.identifier,
        offeringIdentifier: selectedOffer?.offeringIdentifier,
        packageType: selectedOffer?.packageType,
        product: selectedOffer?.product,
      };
      const options = {
        aPackage: productToPurchase,
      };
      const data = await Purchases.purchasePackage(options);
      const purchaserInfo = data.purchaserInfo;

      setIsPurchasing(false);
      return purchaserInfo;
    } catch (error) {
      setIsPurchasing(false);
      showToast(t("common.error"));

      console.error("listenBuy error", error);
    }
  }

  function calculateIntroLengthValue(introLength, introLengthUnit) {
    let introLengthValue = 0;
    if (introLengthUnit == "DAY") {
      introLengthValue = introLength;
    } else if (introLengthUnit == "WEEK") {
      introLengthValue = introLength * 7;
    } else if (introLengthUnit == "MONTH") {
      introLengthValue = introLength * 30;
    }
    return introLengthValue;
  }

  async function checkIfTrialAvailable(product) {
    if (Capacitor.getPlatform() == "ios") {
      const checkElibilityResult =
        await Purchases.checkTrialOrIntroductoryPriceEligibility({
          productIdentifiers: [product?.identifier],
        });

      const statusCode = checkElibilityResult[product?.identifier]?.status;

      if (statusCode == 2) {
        const introLength = product?.introPrice?.periodNumberOfUnits;

        const introLengthUnit = product?.introPrice?.periodUnit;

        let introLengthValue = calculateIntroLengthValue(
          introLength,
          introLengthUnit
        );

        return introLengthValue;
      }
    } else if (Capacitor.getPlatform() == "android") {
      const planHasTrial = product?.subscriptionOptions?.find(
        (option) => option?.freePhase != null
      );

      if (planHasTrial) {
        const introLength = planHasTrial?.freePhase?.billingPeriod?.value;

        const introLengthUnit = planHasTrial?.freePhase?.billingPeriod?.unit;

        let introLengthValue = calculateIntroLengthValue(
          introLength,
          introLengthUnit
        );

        return introLengthValue;
      }
    }
  }

  async function restorePurchases(e) {
    e.preventDefault();
    try {
      await Purchases.restorePurchases();
    } catch (e) {
      console.log("Error restoring purchases", e.message);
    }
  }

  const modalContent = isLoading ? (
    <SkeletonLoader type={"checkBoxList"} amount={3} />
  ) : premium ? (
    <div className="flex flex-col h-auto">
      <div className="flex flex-col h-full items-center justify-center text-center mt-2 pb-4">
        <div className="flex w-16 h-16 ">
          <img
            src={user?.photoURL}
            className="w-16 h-16 rounded-full row-start-1 col-start-1 mb-4 "
            alt=""
          />
        </div>
        <div className="flex items-center text-md font-semibold bg-black text-white rounded-md px-2 text-xs -mt-3 uppercase ">
          {t("premium.title")}
        </div>
        <div className="font-bold pt-4 text-white">{t("premium.welcome")}</div>
        <div className="flex flex-col font-extralight pt-4 text-white px-4 text-sm">
          {t("premium.success")}
        </div>
      </div>
    </div>
  ) : (
    <div className="flex flex-col py-4 h-auto w-full">
      <div className="flex flex-col w-full justify-center items-center px-4">
        <span className="font-Caveat text-4xl text-white">Predictably</span>

        <div className="flex items-center text-md font-semibold bg-black text-white rounded-lg px-2 text-xs uppercase ">
          {t("premium.title")}
        </div>

        <div className="flex flex-col py-4 px-2 mt-4 w-full space-y-2 bg-white/40 rounded-md backdrop-blur-sm">
          <div className="flex flex-row w-full justify-between items-center ">
            <div className="flex text-sm font-light w-full px-2"></div>
            <div className="flex w-20 justify-center shrink-0 ">
              <div className="flex text-sm font-light text-black">
                {t("premium.basic")}
              </div>
            </div>
            <div className="flex text-sm w-24 shrink-0 justify-center">
              <div className="flex text-sm w-fit shrink-0 bg-black h-full items-center justify-center px-2 rounded-full shadow-xl font-semibold text-white uppercase">
                Premium
              </div>
            </div>
          </div>
          {premiumSellingPoints.map((item) => (
            <div className="flex flex-row w-full justify-between items-center  rounded-md h-9">
              <div className="flex text-sm w-full px-2  text-black h-full items-center rounded-l-md font-semibold uppercase tracking-wide ">
                {item?.title}
              </div>
              <div className="flex text-sm w-20 shrink-0 px-2 bg-white/60 h-8  items-center justify-center rounded-l-md stacked-fractions">
                {item?.basicComponent}
              </div>
              <div className="flex text-sm w-24 shrink-0 bg-white h-full items-center justify-center px-2 rounded-r-md shadow-xl font-semibold">
                {item?.premiumComponent}
              </div>
            </div>
          ))}
        </div>
      </div>

      <RadioGroup
        value={selectedOffer}
        onChange={(value) => setSelectedOffer(value)}
      >
        <div className="flex flex-row space-x-2 pt-8 px-4">
          {offerList?.length == 0 ? (
            <div className=" bg-white/60 rounded-md p-4 text-textPrimary flex w-full text-sm  justify-center text-center">
              {t("premium.no_available")}
            </div>
          ) : (
            offerList.map((product) => {
              let monthlyPrice =
                product?.packageType == "ANNUAL"
                  ? new Intl.NumberFormat("en-US", {
                      style: "currency",
                      currencyDisplay: "narrowSymbol",
                      maximumFractionDigits: 2,
                      currency: product?.product?.currencyCode,
                    }).format(product?.product?.price)
                  : new Intl.NumberFormat("en-US", {
                      style: "currency",
                      currencyDisplay: "narrowSymbol",
                      maximumFractionDigits: 2,
                      currency: product?.product?.currencyCode,
                    }).format(product?.product?.price);

              let annualPerMonth = new Intl.NumberFormat("en-US", {
                style: "currency",
                currencyDisplay: "narrowSymbol",
                maximumFractionDigits: 2,
                currency: product?.product?.currencyCode,
              }).format(product?.product?.price / 12);

              let length =
                product?.packageType == "ANNUAL"
                  ? t("premium.length.year", {
                      length: 1,
                    })
                  : t("premium.length.month", {
                      length: 1,
                    });

              return (
                <RadioGroup.Option
                  key={product?.identifier}
                  value={product}
                  className={({ active, checked }) =>
                    classNames(
                      checked ? "border-green-500  " : "border-white",
                      "border-2 bg-white rounded-md shadow-md relative flex cursor-pointer px-2 py-4  focus:outline-none w-full text-textPrimary"
                    )
                  }
                >
                  {({ checked }) => (
                    <>
                      <div
                        className={classNames(
                          checked
                            ? theme?.id === 0
                              ? "text-textPrimary"
                              : "text-secondary900"
                            : "text-secondary900",
                          "items-center justify-between flex w-full h-full text-sm flex-col"
                        )}
                      >
                        {product?.trialAvailable ? (
                          <div className="absolute  top-0 items-center text-sm font-semibold bg-green-500 text-black rounded-md px-3 py-0.5 -mt-3.5  uppercase ">
                            {t("premium.free_trial_header")}
                          </div>
                        ) : (
                          product?.packageType == "ANNUAL" &&
                          annualSaving != undefined &&
                          annualSaving != 0 && (
                            <div className="absolute  top-0 items-center text-sm font-semibold bg-green-500 text-black rounded-md px-3 py-0.5 -mt-3.5  uppercase ">
                              {t("premium.savings", {
                                amount: annualSaving.toFixed(0),
                              })}
                            </div>
                          )
                        )}
                        <div className="flex flex-col justify-center w-full">
                          <div className="flex text-sm uppercase font-semibold justify-center items-center pb-1">
                            {length}
                          </div>
                          <div className="flex justify-center items-center ">
                            <div className="flex justify-center items-center bg-black text-white rounded-md w-fit px-2">
                              <div className="flex text-xl font-semibold ">
                                {monthlyPrice}
                              </div>
                            </div>
                          </div>
                          {product?.packageType == "ANNUAL" && (
                            <div className="flex text-xs font-light justify-center items-center pt-1 pb-2 text-center">
                              <span>
                                {t("premium.amount.yearly", {
                                  amount: annualPerMonth,
                                })}
                              </span>
                              {/*  {annualSaving != undefined &&
                                annualSaving != 0 && (
                                  <span>
                                    {" "}
                                    (
                                    {t("premium.savings", {
                                      amount: annualSaving.toFixed(0),
                                    })}
                                    )
                                  </span>
                                )} */}
                            </div>
                          )}
                        </div>

                        {checked ? (
                          <div className="shrink-0 bg-customGreen rounded-full w-8 h-8 flex justify-center items-center text-white">
                            <CheckIcon className="h-6 w-6 " />
                          </div>
                        ) : (
                          <div className="shrink-0 bg-gray-200 rounded-full w-8 h-8 flex justify-center items-center"></div>
                        )}
                      </div>
                    </>
                  )}
                </RadioGroup.Option>
              );
            })
          )}
        </div>
      </RadioGroup>
      {selectedOffer && selectedOffer?.trialAvailable ? (
        <IonLabel className="text-white text-xs text-center w-full py-2 px-4">
          <div className="flex flex-col ">
            {t("premium.trial_description", {
              days: selectedOffer?.trialLength || 7,
              annualPrice: new Intl.NumberFormat("en-US", {
                style: "currency",
                currencyDisplay: "narrowSymbol",
                maximumFractionDigits: 2,
                currency: selectedOffer?.product?.currencyCode,
              }).format(selectedOffer?.product?.price),
            })}
          </div>
        </IonLabel>
      ) : (
        Capacitor.getPlatform() != "ios" && (
          <div className="flex items-center text-xs font-extralight pt-2 w-full text-center justify-center text-white">
            {t("premium.cancel")}
          </div>
        )
      )}

      {Capacitor.getPlatform() == "ios" && (
        <div className="flex flex-col p-4 h-full  text-xs">
          <span className=" w-full justify-center items-center text-white font-light">
            {t("premium.apple_terms")}
            <a
              className="underline text-white px-1"
              href="https://www.apple.com/legal/internet-services/itunes/dev/stdeula/"
            >
              {t("common.terms")}
            </a>
            {t("common.and")}
            <IonRouterLink
              className="underline text-white pl-1"
              routerLink="/terms"
            >
              <IonLabel>{t("common.privacy")}</IonLabel>
            </IonRouterLink>
          </span>
        </div>
      )}

      <IonButton
        mode="ios"
        fill="clear"
        color="primary"
        onClick={(e) => restorePurchases(e)}
        className="m-0 h-10 customButtonNew"
      >
        <IonLabel className="text-white underline text-sm">
          {t("premium.restore")}
        </IonLabel>
      </IonButton>
    </div>
  );

  return (
    <IonPage
      style={{
        "--image-url": `url(https://firebasestorage.googleapis.com/v0/b/oles-test-project.appspot.com/o/common%2Fstadium.png?alt=media&token=7075d1e6-b2d6-48e1-ae5e-5f12d570d847)`,
      }}
      className="bg-[image:var(--image-url)] bg-no-repeat bg-center bg-cover"
    >
      <IonHeader mode="ios" className="ion-no-border">
        <IonToolbar mode="ios" class="gradientToolbarPrimary">
          <IonTitle>
            <div className="flex items-center justify-center animate-wiggle">
              <AppLogo size={"small"} />
            </div>
          </IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding transparent bg-gradient-to-b from-headerPrimary/80 to-black/50">
        {modalContent}
      </IonContent>
      <IonFooter mode="ios" className="transparent bg-black/90 pb-2 px-3.5">
        <div className="flex gap-0.5 flex-col">
          {!premium && (
            <IonButton
              color="success"
              mode="ios"
              fill="solid"
              expand="block"
              disabled={isPurchasing || selectedOffer == null}
              onClick={(e) => purchase(e)}
              className="customButtonNew rounded-md p-0"
            >
              <IonLabel className="text-black">
                <div className="flex flex-col">
                  {!selectedOffer
                    ? t("premium.cta_trial", {
                        days: 7,
                      })
                    : selectedOffer?.trialAvailable
                    ? t("premium.cta_trial", {
                        days: selectedOffer?.trialLength,
                      })
                    : t("premium.cta")}
                </div>
              </IonLabel>
              <IonLabel slot="end">
                <Md2Loading
                  className={classNames(
                    "ml-1 h-6 w-6",
                    isPurchasing ? "animate-spin" : "hidden"
                  )}
                />
              </IonLabel>
            </IonButton>
          )}

          <IonButton
            color="light"
            mode="ios"
            fill="outline"
            expand="block"
            disabled={isPurchasing}
            onClick={(e) => closeModal()}
            className="customButtonNew rounded-md p-0"
          >
            <IonLabel>
              {premium ? t("feedback.close") : t("premium.secondary")}
            </IonLabel>
          </IonButton>
        </div>
      </IonFooter>
    </IonPage>
  );
}
